/**
 * 策略工具：本机最近访问策略（MRU）。
 * localStorage 中按 scope 存去重 id 列表（新→旧），未出现过的策略保持接口原序接在后面。
 */

const LS_KEY = "longfans.tacticsRecentStrategiesV1";
const MAX_RECENT = 50;

/** 与后端 DROPDOWN_CAP 对齐：下拉最多展示条数 */
export const TACTICS_DROPDOWN_CAP = 5;

type RecentStore = Record<string, string[]>;

function scopeKey(
  spaceId: string,
  spaceProductId: string,
  userId?: string | null
): string {
  const uid = (userId ?? "").trim() || "_";
  return `${uid}:${spaceId.trim()}:${spaceProductId.trim()}`;
}

function readStore(): RecentStore {
  if (typeof localStorage === "undefined") {
    return {};
  }
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const out: RecentStore = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (!Array.isArray(v)) continue;
      const ids = v
        .filter((x): x is string => typeof x === "string" && x.trim().length > 0)
        .map((x) => x.trim());
      if (ids.length) {
        out[k] = ids;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function writeStore(store: RecentStore): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(store));
  } catch {
    // ignore quota / private mode
  }
}

/** 读取某产品下最近访问策略 id（新→旧） */
export function getRecentStrategyIds(
  spaceId: string,
  spaceProductId: string,
  userId?: string | null
): string[] {
  const key = scopeKey(spaceId, spaceProductId, userId);
  return [...(readStore()[key] ?? [])];
}

/** 记录一次本地访问（置顶） */
export function recordLocalStrategyVisit(
  spaceId: string,
  spaceProductId: string,
  strategyId: string,
  userId?: string | null
): void {
  const id = strategyId.trim();
  if (!id || !spaceId.trim() || !spaceProductId.trim()) {
    return;
  }
  const key = scopeKey(spaceId, spaceProductId, userId);
  const store = readStore();
  const prev = store[key] ?? [];
  store[key] = [id, ...prev.filter((x) => x !== id)].slice(0, MAX_RECENT);
  writeStore(store);
}

/**
 * 按本机最近访问优先重排；未访问过的保持原相对顺序接在后段。
 */
export function sortStrategiesByRecentVisit<T extends { id: string }>(
  spaceId: string,
  spaceProductId: string,
  items: T[],
  userId?: string | null
): T[] {
  if (!items.length) {
    return [];
  }
  const recent = getRecentStrategyIds(spaceId, spaceProductId, userId);
  if (!recent.length) {
    return [...items];
  }
  const order = new Map(recent.map((id, i) => [id, i]));
  return [...items].sort((a, b) => {
    const ai = order.has(a.id.trim())
      ? order.get(a.id.trim())!
      : Number.MAX_SAFE_INTEGER;
    const bi = order.has(b.id.trim())
      ? order.get(b.id.trim())!
      : Number.MAX_SAFE_INTEGER;
    return ai - bi;
  });
}
